import type { Feedback } from '@@partner:types:app'
import type { GQL_ID } from '@@partner:types:graphql'

import React, { useState } from 'react'
import {
  Modal,
  Button,
  Input,
  Form,
  Rate,
  Avatar,
  Tag,
  Panel,
  ButtonGroup,
  Divider,
  Message,
  useToaster
} from 'rsuite'
import Zoombox from 'zoombox'

import { NO_PHOTO_IMAGE } from '@@partner:config/image'

import './FeedbackModal.scss'
import { generatePath, Link } from 'react-router-dom'
import { ROUTE_CUSTOMERS_VIEW_CUSTOMER, ROUTE_ORDERS_VIEW_ORDER } from '@@partner:config/routes'
import { usePartner } from '@@partner:helpers/hooks/auth'

export interface FeedbackModalProps {
  feedback: Feedback | null
  open: boolean
  onClose: () => void
  onCommentSubmit?: (feedbackId: GQL_ID, comment: string) => Promise<void>
}

const FormGroup = Form.Group

export default function FeedbackModal ({
  feedback,
  open,
  onClose,
  onCommentSubmit
}: FeedbackModalProps) {
  const { brandSlug } = usePartner()

  const [ comment, setComment ] = useState('')
  const [ isSubmittingComment, setIsSubmittingComment ] = useState(false)
  const [ imageState, setImageState ] = useState({
    isVisible: false,
    slide: 0
  })
  const toaster = useToaster()

  // Обновляем комментарий при изменении feedback
  React.useEffect(() => {
    setComment(feedback?.manage_comment || '')
  }, [ feedback?.manage_comment ])

  const handleShowImage = (slide: number) => {
    setImageState({
      isVisible: true,
      slide
    })
  }

  const handleCommentSubmit = async () => {
    if (!feedback || !comment.trim() || !onCommentSubmit) return

    setIsSubmittingComment(true)
    try {
      await onCommentSubmit(feedback.id, comment.trim())
      setComment('')
      toaster.push(
        <Message type="success">Комментарий успешно добавлен</Message>,
        { placement: 'topCenter' }
      )
    }
    catch (error) {
      toaster.push(
        <Message type="error">Ошибка при добавлении комментария</Message>,
        { placement: 'topCenter' }
      )
    }
    finally {
      setIsSubmittingComment(false)
    }
  }

  const handleCopyFeedback = () => {
    if (!feedback) return

    const feedbackText = `
Отзыв #${feedback.id}
Клиент: ${feedback.customer?.first_name || ''} ${feedback.customer?.last_name || ''}
Магазин: ${feedback.shop?.name || ''} ${feedback.shop?.address || ''}
Общая оценка: ${feedback.general_rating || 0}/5
${feedback.assemble_rating ? `Оценка сборки: ${feedback.assemble_rating}/5` : ''}
${feedback.shipping_rating ? `Оценка доставки: ${feedback.shipping_rating}/5` : ''}
${feedback.product_rating ? `Оценка товара: ${feedback.product_rating}/5` : ''}
${feedback.need_call ? 'Требуется звонок: Да' : 'Требуется звонок: Нет'}

Текст отзыва:
${feedback.review || 'Без текста'}

${feedback.manage_comment ? `Комментарий менеджера:\n${feedback.manage_comment}` : ''}
    `.trim()

    navigator.clipboard.writeText(feedbackText).then(() => {
      toaster.push(
        <Message type="success">Отзыв скопирован в буфер обмена</Message>,
        { placement: 'topCenter' }
      )
    }).catch(() => {
      toaster.push(
        <Message type="error">Ошибка при копировании</Message>,
        { placement: 'topCenter' }
      )
    })
  }

  const images = (feedback?.images ?? []).filter((image) => !!image.url)

  if (!feedback) {
    return null
  }

  return (
    <>
      <Modal
        open={open}
        onClose={onClose}
        size="lg"
        className="FeedbackModal"
      >
        <Modal.Header>
          <Modal.Title>Отзыв #{feedback.id}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="FeedbackModal__Content">
            {/* Информация о клиенте */}
            <Panel header="Информация о клиенте" bordered>
              <div className="FeedbackModal__CustomerInfo">
                <p><strong>Имя:</strong> {feedback.customer?.first_name || ''} {feedback.customer?.last_name || ''}</p>
                {feedback.customer?.phone && (
                  <p><strong>Телефон:</strong> <a href={`tel:+${feedback.customer.phone}`}>+{feedback.customer.phone}</a></p>
                )}
              </div>
              <Button
                appearance='primary'
                as={Link}
                to={() => generatePath(ROUTE_CUSTOMERS_VIEW_CUSTOMER, { brandSlug, customerId: feedback.customer.id })}
                size="xs"
              >
                Открыть карточку клиента
              </Button>
            </Panel>

            {/* Информация о магазине */}
            {feedback.shop && (
              <Panel header="Информация о магазине" bordered>
                <div className="FeedbackModal__ShopInfo">
                  <p><strong>Название:</strong> {feedback.shop.name}</p>
                  <p><strong>Адрес:</strong> {feedback.shop.address}</p>
                </div>
              </Panel>
            )}

            {/* Оценки */}
            <Panel header="Оценки" bordered>
              <div className="FeedbackModal__Ratings">
                <div className="FeedbackModal__Rating">
                  <span>Общая оценка:</span>
                  <Rate
                    value={feedback.general_rating || 0}
                    readOnly
                    size="sm"
                  />
                  <span>({feedback.general_rating || 0}/5)</span>
                </div>
                {feedback.assemble_rating && (
                  <div className="FeedbackModal__Rating">
                    <span>Оценка сборки:</span>
                    <Rate
                      value={feedback.assemble_rating}
                      readOnly
                      size="sm"
                    />
                    <span>({feedback.assemble_rating}/5)</span>
                  </div>
                )}
                {feedback.shipping_rating && (
                  <div className="FeedbackModal__Rating">
                    <span>Оценка доставки:</span>
                    <Rate
                      value={feedback.shipping_rating}
                      readOnly
                      size="sm"
                    />
                    <span>({feedback.shipping_rating}/5)</span>
                  </div>
                )}
                {feedback.product_rating && (
                  <div className="FeedbackModal__Rating">
                    <span>Оценка товара:</span>
                    <Rate
                      value={feedback.product_rating}
                      readOnly
                      size="sm"
                    />
                    <span>({feedback.product_rating}/5)</span>
                  </div>
                )}
              </div>
            </Panel>

            {/* Текст отзыва */}
            <Panel header="Текст отзыва" bordered>
              <div className="FeedbackModal__ReviewText">
                {feedback.review ? (
                  <p style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {feedback.review}
                  </p>
                ) : (
                  <em>Текст отзыва отсутствует</em>
                )}
              </div>
            </Panel>

            {/* Дополнительная информация */}
            <Panel header="Дополнительная информация" bordered>
              <div className="FeedbackModal__AdditionalInfo">
                <p>
                  <strong>Требуется звонок:</strong>{' '}
                  {feedback.need_call ? (
                    <Tag color="red" size="sm">Да</Tag>
                  ) : (
                    <Tag color="green" size="sm">Нет</Tag>
                  )}
                </p>
                {feedback.order_id != null && (
                  <Link
                    to={generatePath(ROUTE_ORDERS_VIEW_ORDER, { brandSlug, orderId: feedback.order_id })}
                  ><strong>ID заказа:</strong> {feedback.order_id}</Link>
                )}
              </div>
            </Panel>

            {/* Изображения */}
            {images.length > 0 && (
              <Panel header="Изображения" bordered>
                <div className="FeedbackModal__Images">
                  {images.map((image, index) => (
                    <Avatar
                      key={`feedback-image-${index}`}
                      src={image.url ?? NO_PHOTO_IMAGE}
                      size="lg"
                      onClick={() => handleShowImage(index)}
                      style={{ cursor: 'pointer', margin: '0 4px 4px 0' }}
                    />
                  ))}
                </div>
              </Panel>
            )}

            {/* Комментарий менеджера */}
            {feedback.manage_comment && (
              <Panel header="Комментарий менеджера" bordered>
                <div className="FeedbackModal__ManagerComment">
                  <p style={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>
                    {feedback.manage_comment}
                  </p>
                </div>
              </Panel>
            )}

            {/* Форма добавления комментария */}
            {onCommentSubmit && (
              <Panel header="Редактировать комментарий" bordered>
                <Form>
                  <FormGroup>
                    <Input
                      as="textarea"
                      rows={3}
                      placeholder="Введите комментарий для покупателя..."
                      value={comment}
                      onChange={setComment}
                    />
                  </FormGroup>
                  <FormGroup>
                    <Button
                      appearance="primary"
                      onClick={handleCommentSubmit}
                      loading={isSubmittingComment}
                      disabled={!comment.trim()}
                    >
                      {feedback.manage_comment ? 'Обновить комментарий' : 'Добавить комментарий'}
                    </Button>
                  </FormGroup>
                </Form>
              </Panel>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <ButtonGroup>
            <Button appearance="primary" onClick={handleCopyFeedback}>
              Скопировать
            </Button>
            <Button onClick={onClose} appearance="subtle">
              Закрыть
            </Button>
          </ButtonGroup>
        </Modal.Footer>
      </Modal>

      {/* Зум изображений */}
      {images.length > 0 && (
        <Zoombox
          active={imageState.isVisible}
          maskClosable
          lockBodyScroll
          enableImageDragBeta
          images={images.map((image) => ({
            src: image.url as string,
            caption: ''
          }))}
          selectedImage={imageState.slide}
          setActive={(active) => {
            setImageState((state) => ({ ...state, isVisible: !!active }))
          }}
        />
      )}
    </>
  )
}